'use client'

import { useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/contexts/auth-context'
import { Github, Loader2 } from 'lucide-react'
import { useLanguage } from '@/contexts/language-context'

interface LoginModalProps {
  isOpen: boolean
  onClose: () => void
}

export function LoginModal({ isOpen, onClose }: LoginModalProps) {
  const { signInWithGoogle, signInWithGithub, isLoading } = useAuth()
  const [isGoogleLoading, setIsGoogleLoading] = useState(false)
  const [isGithubLoading, setIsGithubLoading] = useState(false)
  const { t } = useLanguage()

  const handleGoogleSignIn = async () => {
    setIsGoogleLoading(true)
    await signInWithGoogle()
    // 注意：不需要设置为false，因为会重定向到授权页面
  }

  const handleGithubSignIn = async () => {
    setIsGithubLoading(true)
    await signInWithGithub()
    // 注意：不需要设置为false，因为会重定向到授权页面
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[425px] bg-white !bg-white shadow-lg" style={{ backgroundColor: 'white' }}>
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">{t('auth.signIn') || 'Sign In'}</DialogTitle>
          <DialogDescription> </DialogDescription>
        </DialogHeader>
        
        <div className="grid gap-4 py-4">
          <button
            className="flex items-center justify-center gap-2 h-12 w-full rounded-md bg-white text-black hover:bg-gray-100 font-medium transition-colors relative"
            onClick={handleGoogleSignIn}
            disabled={isLoading || isGoogleLoading || isGithubLoading}
            style={{ border: '2px solid #d1d5db' }}
          >
            {isGoogleLoading ? (
              <Loader2 className="mr-2 h-5 w-5 animate-spin text-gray-500" />
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                <path d="M1 1h22v22H1z" fill="none" />
              </svg>
            )}
            {isGoogleLoading ? t('auth.redirecting') || '正在跳转...' : t('auth.continueWithGoogle') || 'Continue with Google'}
          </button>
          
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-gray-300"></span>
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-2 text-muted-foreground">{t('auth.or') || 'OR'}</span>
            </div>
          </div>
          
          <button
            className="flex items-center justify-center gap-2 h-12 w-full rounded-md bg-white text-black hover:bg-gray-100 font-medium transition-colors"
            onClick={handleGithubSignIn}
            disabled={isLoading || isGoogleLoading || isGithubLoading}
            style={{ border: '2px solid #d1d5db' }}
          >
            {isGithubLoading ? (
              <Loader2 className="mr-2 h-5 w-5 animate-spin text-gray-500" />
            ) : (
              <Github className="h-5 w-5" />
            )}
            {isGithubLoading ? t('auth.redirecting') || '正在跳转...' : t('auth.continueWithGithub') || 'Continue with GitHub'}
          </button>
        </div>
        <p className="mt-2 text-[11px] leading-snug text-gray-500 text-center px-2">
          {t('auth.consent.prefix')}{' '}
          <a href="/terms-of-service" target="_blank" className="underline hover:text-gray-700">{t('auth.consent.terms')}</a>{' '}
          {t('auth.consent.and')}{' '}
          <a href="/privacy-policy" target="_blank" className="underline hover:text-gray-700">{t('auth.consent.privacy')}</a>.
        </p>
      </DialogContent>
    </Dialog>
  )
} 